import React from 'react';
import { Link } from 'react-router-dom';

import {
  Users,
  FileText,
  Calendar,
  Settings,
  Plus,
  Download,
  Activity,
  AlertCircle,
  Upload,
  BookOpen,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  MessageCircle,
  Shield,
  Mail,
  UserCheck,
  Sparkles,
  Megaphone,
  MessageSquare, Newspaper, CalendarClock, CreditCard, Star, Rocket, Building2 } from 'lucide-react';
import { MessagesNavButton } from '../messaging/MessagesNavButton';
import { NewBadge } from '../NewBadge';
import { can } from '../RequirePermission';

export const DashboardSidebar = ({ currentView, expandedSections, handleExportData, handleImportPatients, importOpen, loadDashboard, navigate, practice, setCurrentView, setImportOpen, setShowAnnouncementsAdmin, setShowSupportModal, setShowTutorialsModal, sidebarOpen, setSidebarOpen, toggleSection, user }) => (
  <>
            {sidebarOpen && <div className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-backdrop" />}
            <div data-testid="dashboard-sidebar"
              onClickCapture={(e) => { if (window.innerWidth < 1024 && e.target.closest('a, button') && !e.target.closest('[data-section-toggle]')) setTimeout(() => setSidebarOpen(false), 0); }}
              className={`bg-white border-r shadow-lg flex-col ${sidebarOpen ? 'flex' : 'hidden'} fixed inset-y-0 left-0 z-50 w-[85vw] max-w-sm lg:static lg:z-auto lg:w-96 lg:max-w-none lg:flex`}>
              <div className={`flex flex-col h-full ${sidebarOpen ? 'flex' : 'hidden'}`}>
                {/* Navigation Header */}
                <div className="p-4 flex-shrink-0" style={{ backgroundColor: '#0586ba' }}>
                  <div className="relative"><h2 className="text-white font-semibold text-2xl lg:text-3xl text-center">Navigation</h2>
                  <button type="button" data-section-toggle onClick={() => setSidebarOpen(false)} aria-label="Close menu" data-testid="sidebar-close-btn" className="lg:hidden absolute right-0 top-1/2 -translate-y-1/2 text-white text-3xl leading-none px-2">&times;</button></div>
                </div>

                {/* Scrollable Navigation Items with ALWAYS VISIBLE scrollbar */}
                <div className="p-2 flex-1 nav-scrollbar overflow-y-auto lg:max-h-[calc(100vh-180px)]" style={{ minHeight: '300px', overflowY: 'auto' }}>

                  <button type="button" onClick={() => window.dispatchEvent(new Event('practcom:open-welcome-checklist'))}
                    className="w-full flex items-center gap-3 p-3 mb-2 rounded-lg bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 text-left"
                    data-testid="sidebar-getting-started-btn">
                    <Rocket className="h-6 w-6 text-emerald-700 shrink-0" />
                    <span className="text-lg font-semibold text-emerald-900">Getting Started</span>
                  </button>

                  {/* Daily Tasks */}
                  <div className="mb-2">
                    <button 
                      data-section-toggle
                      onClick={() => toggleSection('dailyTasks')}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <div className="flex items-center">
                        <Calendar className="h-8 w-8 mr-4 text-blue-600" />
                        <span className="text-gray-700 font-semibold text-2xl">Daily Tasks</span>
                      </div>
                      {expandedSections.dailyTasks ? (
                        <ChevronDown className="h-6 w-6 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-6 w-6 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.dailyTasks && (
                      <div className="ml-6 mt-2 space-y-2">
                        <Link
                          to="/add-patient"
                          className="w-full flex items-center p-4 hover:bg-blue-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <Plus className="h-7 w-7 mr-4 text-blue-600" />
                          Add Patient
                        </Link>
                        <Link
                          to="/assign-procedure"
                          className="w-full flex items-center p-4 hover:bg-blue-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <FileText className="h-7 w-7 mr-4 text-green-600" />
                          Assign Procedure
                        </Link>
                        <Link
                          to="/assign-informed-consent"
                          className="w-full flex items-center p-4 hover:bg-blue-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <Shield className="h-7 w-7 mr-4 text-indigo-600" />
                          Assign Informed Consent
                        </Link>
                        <Link
                          to="/assign-compliance-form"
                          className="w-full flex items-center p-4 hover:bg-amber-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <FileText className="h-7 w-7 mr-4 text-amber-600" />
                          Assign Compliance Form
                        </Link>
                        <Link
                          to="/med-cards/assign"
                          className="w-full flex items-center p-4 hover:bg-blue-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <FileText className="h-7 w-7 mr-4 text-orange-600" />
                          Assign Med Card
                        </Link>
                        <button
                          type="button"
                          onClick={() => {
                            console.log('Referral Management clicked!');
                            window.location.href = '/referral-management';
                          }}
                          className="w-full flex items-center p-4 hover:bg-blue-50 rounded-md text-left text-lg text-gray-700 cursor-pointer border-0 bg-transparent"
                        >
                          <UserCheck className="h-7 w-7 mr-4 text-teal-600" />
                          Referral Management
                        </button>
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Assign Problems clicked!');
                            window.location.pathname = '/assign-problems-emergencies';
                          }}
                          className="w-full flex items-center p-4 hover:bg-blue-50 rounded-md text-left text-lg text-gray-700 cursor-pointer border-0 bg-transparent"
                        >
                          <AlertCircle className="h-7 w-7 mr-4 text-red-600" />
                          Assign Problems & Emergencies
                        </button>
    {can(user, 'clinical') && (
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('SmileDesign AI clicked!');
                            window.location.href = '/smile-design';
                          }}
                          className="w-full flex items-center justify-between p-4 hover:bg-purple-50 rounded-md text-left text-lg text-gray-700 group cursor-pointer border-0 bg-transparent"
                          data-testid="smile-design-nav"
                        >
                          <div className="flex items-center">
                            <Sparkles className="h-7 w-7 mr-4 text-purple-600" />
                            <span>SmileDesign AI</span>
                          </div>
                          <span className="text-xs bg-gradient-to-r from-purple-500 to-blue-500 text-white px-2 py-1 rounded-full">NEW</span>
                        </button>
    )}
                      </div>
                    )}
                  </div>

                  {/* Resources */}
    {can(user, 'settings') && (
                  <div className="mb-2">
                    <button 
                      onClick={() => navigate('/practice/applicants')}
                      data-testid="sidebar-hire-staff-btn"
                      className="w-full flex items-center justify-between p-4 hover:bg-blue-50 rounded-lg transition-colors mb-2 group"
                    >
                      <div className="flex items-center">
                        <svg className="h-8 w-8 mr-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <span className="text-gray-700 font-semibold text-2xl">Hire Staff</span>
                      </div>
                      <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700">New</span>
                    </button>
                  </div>
    )}

                  {/* Resources */}
                  <div className="mb-2">
                    <button 
                      data-section-toggle
                      onClick={() => toggleSection('resources')}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <div className="flex items-center">
                        <BookOpen className="h-8 w-8 mr-4 text-purple-600" />
                        <span className="text-gray-700 font-semibold text-2xl">Resources</span>
                      </div>
                      {expandedSections.resources ? (
                        <ChevronDown className="h-6 w-6 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-6 w-6 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.resources && (
                      <div className="ml-6 mt-2 space-y-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/procedure-library');
                          }}
                          className="w-full flex items-center p-4 hover:bg-purple-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <FileText className="h-7 w-7 mr-4 text-purple-600" />
                          Procedure Library
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/informed-consent-library');
                          }}
                          className="w-full flex items-center p-4 hover:bg-red-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <FileText className="h-7 w-7 mr-4 text-red-600" />
                          Informed Consents
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/compliance-forms-library');
                          }}
                          className="w-full flex items-center p-4 hover:bg-amber-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <Shield className="h-7 w-7 mr-4 text-amber-600" />
                          Compliance Forms Library
                        </button>
    {can(user, 'settings') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/follow-up-settings');
                          }}
                          className="w-full flex items-center p-4 hover:bg-emerald-50 rounded-md text-left text-lg text-gray-700"
                          data-testid="followup-settings-link"
                        >
                          <MessageCircle className="h-7 w-7 mr-4 text-emerald-600" />
                          Check-In Settings
                        </button>
    )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/med-cards/library');
                          }}
                          className="w-full flex items-center p-4 hover:bg-orange-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <FileText className="h-7 w-7 mr-4 text-orange-600" />
                          Med Card Library
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/management-forms/library');
                          }}
                          className="w-full flex items-center p-4 hover:bg-purple-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <FileText className="h-7 w-7 mr-4 text-purple-600" />
                          Management Forms Library
                        </button>
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Problems & Emergencies Library clicked!');
                            window.location.pathname = '/problems-emergencies/library';
                          }}
                          className="w-full flex items-center p-4 hover:bg-red-50 rounded-md text-left text-lg text-gray-700 cursor-pointer border-0 bg-transparent"
                        >
                          <AlertCircle className="h-7 w-7 mr-4 text-red-600" />
                          Problems & Emergencies
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/ask-dan');
                          }}
                          className="w-full flex items-center p-4 hover:bg-cyan-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <MessageCircle className="h-7 w-7 mr-4 text-cyan-600" />
                          Ask Dr. G
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/tooth-guides');
                          }}
                          className="w-full flex items-center p-4 hover:bg-purple-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <BookOpen className="h-7 w-7 mr-4 text-purple-600" />
                          Tooth Guides For Patients
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/patient-ecards');
                          }}
                          className="w-full flex items-center p-4 hover:bg-sky-50 rounded-md text-left text-lg text-gray-700"
                          data-testid="resources-patient-ecards-link"
                        >
                          <Mail className="h-7 w-7 mr-4 text-sky-600" />
                          Patient Education Studio
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Management */}
                  <div className="mb-2">
                    <button 
                      data-section-toggle
                      onClick={() => toggleSection('management')}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <div className="flex items-center">
                        <Settings className="h-8 w-8 mr-4 text-indigo-600" />
                        <span className="text-gray-700 font-semibold text-2xl">Management</span>
                      </div>
                      {expandedSections.management ? (
                        <ChevronDown className="h-6 w-6 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-6 w-6 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.management && (
                      <div className="ml-6 mt-2 space-y-2">
    {can(user, 'billing') && (
                        <button
                          onClick={() => navigate('/plans-billing')}
                          className="w-full flex items-center p-4 hover:bg-emerald-50 rounded-md text-left text-lg text-gray-700"
                          data-testid="nav-current-plan"
                        >
                          <CreditCard className="h-7 w-7 mr-4 text-emerald-600" />
                          Current Plan
                        </button>
    )}
    {can(user, 'settings') && (
                        <button
                          onClick={() => navigate('/practice-settings')}
                          className="w-full flex items-center p-4 hover:bg-gray-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <Settings className="h-7 w-7 mr-4 text-gray-600" />
                          Practice Settings
                        </button>
    )}
                        <button
                          onClick={() => navigate('/offices')}
                          className="w-full flex items-center p-4 hover:bg-emerald-50 rounded-md text-left text-lg text-gray-700"
                          data-testid="nav-offices"
                        >
                          <Building2 className="h-7 w-7 mr-4 text-emerald-600" />
                          Offices
                        </button>
    {(sessionStorage.getItem('practcomOfficeId') || (user?.role === 'practice_staff' && user?.officeId)) && (
                        <button
                          onClick={() => navigate(`/offices/${sessionStorage.getItem('practcomOfficeId') || user.officeId}/settings`)}
                          className="w-full flex items-center p-4 hover:bg-emerald-50 rounded-md text-left text-lg text-gray-700"
                          data-testid="nav-office-settings"
                        >
                          <Settings className="h-7 w-7 mr-4 text-emerald-600" />
                          Office Settings
                        </button>
    )}
    {can(user, 'staff') && (
                        <button
                          onClick={() => navigate('/staff-management')}
                          className="w-full flex items-center p-4 hover:bg-emerald-50 rounded-md text-left text-lg text-gray-700"
                          data-testid="nav-manage-staff"
                        >
                          <Users className="h-7 w-7 mr-4 text-emerald-600" />
                          Manage Staff Accounts
                        </button>
    )}
    {can(user, 'settings') && (
                        <button
                          onClick={() => navigate('/dentist-management')}
                          className="w-full flex items-center p-4 hover:bg-indigo-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <Users className="h-7 w-7 mr-4 text-indigo-600" />
                          Manage Dentists
                        </button>
    )}
                        <button
                          onClick={() => navigate('/referral-dentists')}
                          className="w-full flex items-center p-4 hover:bg-purple-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <Users className="h-7 w-7 mr-4 text-purple-600" />
                          Manage Referral Dentists
                        </button>
                        <button
                          onClick={() => navigate('/patient-management')}
                          className="w-full flex items-center p-4 hover:bg-teal-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <Users className="h-7 w-7 mr-4 text-teal-600" />
                          Manage Patients
                        </button>
                        <MessagesNavButton onClick={() => navigate('/messages')} />
                        <button
                          onClick={() => navigate('/reviews')}
                          className="w-full flex items-center p-4 hover:bg-amber-50 rounded-md text-left text-lg text-gray-700"
                          data-testid="sidebar-reviews-btn"
                        >
                          <Star className="h-7 w-7 mr-4 text-amber-500" />
                          Patient Reviews
                          <NewBadge className="ml-2" testId="sidebar-reviews-new-badge" />
                        </button>
                        <button
                          onClick={() => navigate('/newsletter')}
                          className="w-full flex items-center p-4 hover:bg-teal-50 rounded-md text-left text-lg text-gray-700"
                          data-testid="nav-newsletter"
                        >
                          <Newspaper className="h-7 w-7 mr-4 text-teal-600" />
                          Patient Newsletter
                        </button>
                        <button
                          onClick={() => navigate('/newsletter/scheduler')}
                          className="w-full flex items-center p-4 hover:bg-violet-50 rounded-md text-left text-lg text-gray-700"
                          data-testid="nav-newsletter-scheduler"
                        >
                          <CalendarClock className="h-7 w-7 mr-4 text-violet-600" />
                          Newsletter Scheduler
                        </button>
    {can(user, 'settings') && (
                        <button
                          onClick={() => navigate('/website-chatbot')}
                          className="w-full flex items-center p-4 hover:bg-blue-50 rounded-md text-left text-lg text-gray-700"
                          data-testid="dashboard-website-chatbot-link"
                        >
                          <MessageSquare className="h-7 w-7 mr-4 text-blue-600" />
                          Website Chatbot
                        </button>
    )}
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleImportPatients(e);
                          }}
                          type="button"
                          className="w-full flex items-center p-4 hover:bg-emerald-50 rounded-md text-left text-lg text-gray-700 cursor-pointer"
                          data-testid="import-patients-btn"
                        >
                          <Upload className="h-7 w-7 mr-4 text-emerald-600" />
                          Import Patients
                        </button>
                        <button
                          onClick={() => setShowAnnouncementsAdmin(true)}
                          className="w-full flex items-center gap-4 p-5 mt-1 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300 transition-all"
                          data-testid="updates-news-btn"
                        >
                          <Megaphone className="h-8 w-8 text-blue-600 flex-shrink-0" />
                          <div>
                            <p className="font-bold text-blue-900 text-lg">Updates & News</p>
                            <p className="text-sm text-blue-600">View updates & request features</p>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>

    {/* Analytics */}
    {can(user, 'analytics') && (
                  <div className="mb-2">
                    <button 
                      data-section-toggle
                      onClick={() => toggleSection('analytics')}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <div className="flex items-center">
                        <Activity className="h-8 w-8 mr-4 text-cyan-600" />
                        <span className="text-gray-700 font-semibold text-2xl">Analytics</span>
                      </div>
                      {expandedSections.analytics ? (
                        <ChevronDown className="h-6 w-6 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-6 w-6 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.analytics && (
                      <div className="ml-6 mt-2 space-y-2">
                        <button
                          onClick={handleExportData}
                          className="w-full flex items-center p-4 hover:bg-gray-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <Download className="h-7 w-7 mr-4 text-gray-600" />
                          Export Data
                        </button>
                      </div>
                    )}
                  </div>
    )}

                  {/* Support */}
                  <div className="mb-2">
                    <button 
                      data-section-toggle
                      onClick={() => toggleSection('support')}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <div className="flex items-center">
                        <HelpCircle className="h-8 w-8 mr-4 text-orange-600" />
                        <span className="text-gray-700 font-semibold text-2xl">Support</span>
                      </div>
                      {expandedSections.support ? (
                        <ChevronDown className="h-6 w-6 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-6 w-6 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.support && (
                      <div className="ml-6 mt-2 space-y-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSupportModal(true);
                          }}
                          className="w-full flex items-center p-4 hover:bg-orange-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <HelpCircle className="h-7 w-7 mr-4 text-orange-600" />
                          Get Help
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newView = currentView === 'support' ? 'dashboard' : 'support';
                            console.log('Support History clicked. Current view:', currentView, '→ New view:', newView);
                            setCurrentView(newView);
                          }}
                          className="w-full flex items-center p-4 hover:bg-gray-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <FileText className="h-7 w-7 mr-4 text-gray-600" />
                          Support History
                        </button>
                        <button
                          onClick={() => window.open('/STAFF_TRAINING_MANUAL.html', '_blank')}
                          className="w-full flex items-center p-4 hover:bg-blue-50 rounded-md text-left text-lg text-gray-700"
                          data-testid="staff-training-manual-btn"
                        >
                          <BookOpen className="h-7 w-7 mr-4 text-blue-600" />
                          Staff Training Manual
                        </button>
                        <button
                          onClick={() => window.open('/STAFF_TRAINING_MANUAL.html?print=1', '_blank')}
                          className="w-full flex items-center p-4 pl-8 hover:bg-blue-50 rounded-md text-left text-base text-gray-600"
                          data-testid="staff-training-manual-pdf-btn"
                        >
                          <Download className="h-6 w-6 mr-4 text-blue-500" />
                          Download Manual (PDF)
                        </button>
                        <button
                          onClick={() => setShowTutorialsModal(true)}
                          className="w-full flex items-center p-4 hover:bg-green-50 rounded-md text-left text-lg text-gray-700"
                        >
                          <BookOpen className="h-7 w-7 mr-4 text-green-600" />
                          Tutorials
                        </button>
                      </div>
                    )}
                  </div>

                </div>

                {/* Marketing Logo at Bottom - Responsive sizing based on screen size */}
                <div className="p-3 pt-4 md:p-4 md:pt-6 lg:p-5 lg:pt-8 border-t border-gray-200 bg-white flex-shrink-0">
                  <div className="flex justify-center">
                    <img 
                      src="/practcom-logo.png"
                      alt="PractCom"
                      className="w-32 sm:w-36 md:w-40 lg:w-48 xl:w-52 h-auto object-contain max-h-16 md:max-h-20 lg:max-h-24"
                      style={{
                        filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))'
                      }}
                    />
                  </div>
                  <p className="text-center text-xs md:text-sm text-gray-400 mt-1.5 md:mt-2 font-medium">
                    Powered by PractCom
                  </p>
                </div>
              </div>
            </div>
  </>
);
